import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface KeumKangKwonProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function KeumKangKwon({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: KeumKangKwonProps) {
  if (!["BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["keum_kang_kwon_slow", "keum_kang_kwon"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["keum_kang_kwon_slow", "keum_kang_kwon"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  const patternInstructions = (
    <div>
      <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Instructions</h4>
      <ol className="list-decimal list-inside space-y-2">
        <li className="text-gray-700">Start in Ready Stance (Joonbi)</li>
        <li className="text-gray-700">Begin with diamond-hard striking sequence</li>
        <li className="text-gray-700">Execute supreme power generation techniques</li>
        <li className="text-gray-700">
          Demonstrate advanced stance mastery <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Perform complex combination sequences</li>
        <li className="text-gray-700">Show masterful energy control</li>
        <li className="text-gray-700">Execute ultimate technical precision movements</li>
        <li className="text-gray-700">
          Complete final sequence with perfect form <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Return to Ready Stance (Joonbi)</li>
      </ol>
    </div>
  );

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">KeumKangKwon — Diamond Fist</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="KeumKangKwon">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="details" className={triggerClass}>
              Details
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="mb-4 text-gray-700">
                KeumKangKwon, meaning &quot;Diamond Fist&quot;, is a grandmaster-level pattern that represents the ultimate refinement of martial arts
                technique. This pattern embodies the indestructible nature of the diamond, combining supreme hardness with brilliant clarity of execution.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="details" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Number of Movements: 48</li>
                <li>Pattern Diagram: Diamond (◆)</li>
                <li>Starting Position: Ready Stance</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Diamond-hard striking methods</li>
                <li>Supreme power generation</li>
                <li>Advanced stance mastery</li>
                <li>Complex combination sequences</li>
                <li>Masterful energy control</li>
                <li>Ultimate technical precision</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Supreme technical mastery</li>
                <li>Ultimate power refinement</li>
                <li>Complete body control</li>
                <li>Spiritual enlightenment</li>
                <li>Energy mastery</li>
                <li>Perfect form execution</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            {patternInstructions}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          <p className="mb-4 text-gray-700">
            KeumKangKwon, meaning &quot;Diamond Fist&quot;, is a grandmaster-level pattern that represents the ultimate refinement of martial arts
            technique. This pattern embodies the indestructible nature of the diamond, combining supreme hardness with brilliant clarity of execution.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Number of Movements: 48</li>
            <li>Pattern Diagram: Diamond (◆)</li>
            <li>Starting Position: Ready Stance</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Diamond-hard striking methods</li>
            <li>Supreme power generation</li>
            <li>Advanced stance mastery</li>
            <li>Complex combination sequences</li>
            <li>Masterful energy control</li>
            <li>Ultimate technical precision</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Supreme technical mastery</li>
            <li>Ultimate power refinement</li>
            <li>Complete body control</li>
            <li>Spiritual enlightenment</li>
            <li>Energy mastery</li>
            <li>Perfect form execution</li>
          </ul>
        </div>

        {patternInstructions}
      </div>
    </div>
  );
}
